import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getRefunds, deleteRefund, updateRefundStatus, updateRefundMailStatus } from '../api/refundApi';
import { getStatuses } from '../api/statusApi';
import roleApi from '../api/roleApi';
import { AuthContext } from '../context/AuthContext';
import { usePermission } from '../context/PermissionContext';
import { toast } from 'react-toastify';
import { format, isAfter, isBefore, isEqual, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';
import { getStatusClass, getStatusStyle } from '../utils/statusUtils';
import { 
  PlusCircle, 
  Search, 
  Eye, 
  Trash2,
  Edit,
  Filter,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Image,
  Plus,
  Info,
  Calendar,
  AlertTriangle,
  Copy,
  Mail
} from 'lucide-react';
import PermissionGuard from '../components/common/PermissionGuard';

// Durum güncelleme modalı
const StatusUpdateModal = React.memo(({ refund, onClose, onUpdate, statuses }) => {
  const [newStatusId, setNewStatusId] = useState(refund.status_id.toString());
  const [isLoading, setIsLoading] = useState(false);
  
  const handleSubmit = async () => {
    if (newStatusId === refund.status_id.toString()) {
      onClose();
      return;
    }
    
    setIsLoading(true);
    try {
      await onUpdate(refund.id, newStatusId);
      toast.success('Durum başarıyla güncellendi');
      onClose();
    } catch (error) {
      toast.error(`Durum güncellenirken hata: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">Durum Güncelle</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            &times;
          </button>
        </div>
        
        <div className="p-4">
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-1">İade ID: #{refund.id}</p>
            <p className="font-medium">{refund.refund_name}</p>
            <p className="text-sm text-gray-600">{refund.payment_method_name} - {refund.amount} TL</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Durum
            </label>
            <select
              value={newStatusId}
              onChange={(e) => setNewStatusId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {statuses.map(status => (
                <option key={status.id} value={status.id.toString()}>
                  {status.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="p-4 border-t bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 mr-2"
          >
            İptal
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-300 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Güncelleniyor...' : 'Güncelle'}
          </button>
        </div>
      </div>
    </div>
  );
});

// Dekont görüntüleme modalı
const ImageViewerModal = React.memo(({ imageUrl, onClose }) => {
  // Resim URL'sini işleyen yardımcı fonksiyon
  const getFormattedImageUrl = (url) => {
    if (!url) {
      return '';
    }
    
    // URL zaten tam yol içeriyorsa olduğu gibi kullan
    if (url.startsWith('http')) {
      return url;
    }
    
    // URL /uploads ile başlıyorsa olduğu gibi kullan (yeni format)
    if (url.startsWith('/uploads/')) {
      return url;
    }
    
    // URL uploads/ ile başlıyorsa / öneki ekle
    if (url.startsWith('uploads/')) {
      return `/${url}`;
    }
    
    // Eski format kontrolü - geçiş dönemi için (client/public içerenler)
    if (url.startsWith('/client/public/')) {
      // /client/public/uploads/... -> /uploads/...
      return url.replace('/client/public/uploads/', '/uploads/').replace('/client/public/', '/');
    }
    
    // Aksi takdirde /uploads/ öneki ekle
    return `/uploads/${url}`;
  };

  const formattedUrl = getFormattedImageUrl(imageUrl);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full relative">
        {/* Make close button fixed at top-right */}
        <div className="sticky top-0 p-4 border-b flex justify-between items-center bg-white rounded-t-lg z-10">
          <h3 className="text-lg font-medium">Dekont Görüntüle</h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100"
          >
            &times;
          </button>
        </div>
        
        {/* Add max-height constraint and overflow for scrolling */}
        <div className="p-6 overflow-auto max-h-[calc(90vh-120px)]">
          {formattedUrl ? (
            <img 
              src={formattedUrl} 
              alt="Dekont" 
              className="w-full h-auto max-h-[70vh] object-contain rounded mx-auto" 
              loading="lazy"
              onError={(e) => {
                e.target.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>';
                e.target.alt = "Resim yüklenemedi";
                e.target.style.padding = "2rem";
                e.target.style.opacity = "0.5";
              }} 
            />
          ) : (
            <div className="flex flex-col items-center justify-center p-8 text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <p className="mt-4 text-center">Görüntülenecek dekont bulunamadı.</p>
            </div>
          )}
        </div>
        
        <div className="p-4 border-t bg-gray-50 flex justify-end sticky bottom-0 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
});

// Silme onay modalı
const DeleteConfirmModal = React.memo(({ refund, onClose, onConfirm }) => {
  const [isLoading, setIsLoading] = useState(false);
  
  const handleDelete = async () => {
    setIsLoading(true);
    try {
      await onConfirm(refund.id);
      toast.success('İade başarıyla silindi');
    } catch (error) {
      toast.error(`İade silinirken hata: ${error.message}`);
    } finally {
      setIsLoading(false);
      onClose();
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">İadeyi Sil</h3>
        </div>
        
        <div className="p-6">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <Trash2 className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-center text-gray-900 mb-2">
            İadeyi silmek istediğinize emin misiniz?
          </h3>
          <p className="text-sm text-center text-gray-500">
            #{refund.id} numaralı iade kaydı kalıcı olarak silinecektir. Bu işlem geri alınamaz.
          </p>
        </div>
        
        <div className="p-4 border-t bg-gray-50 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 mr-2"
          >
            İptal
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={isLoading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-red-300 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Siliniyor...' : 'Sil'}
          </button>
        </div>
      </div>
    </div>
  );
});

const RefundListPage = () => {
  const { user } = useContext(AuthContext);
  const { hasPermission } = usePermission();
  const navigate = useNavigate();
  
  const tableContainerRef = useRef(null);
  
  const [refunds, setRefunds] = useState([]);
  const [filteredRefunds, setFilteredRefunds] = useState([]);
  const [duplicateRefundGroups, setDuplicateRefundGroups] = useState({});
  const [loading, setLoading] = useState(true);
  
  // SessionStorage anahtarı
  const FILTERS_STORAGE_KEY = 'refund_list_filters';
  
  // SessionStorage'dan filtreleri yükle
  const loadFiltersFromStorage = () => {
    try {
      const savedFilters = sessionStorage.getItem(FILTERS_STORAGE_KEY);
      return savedFilters ? JSON.parse(savedFilters) : {};
    } catch (error) {
      console.error('Filtreler yüklenirken hata:', error);
      return {};
    }
  };
  
  // Filtreleri sessionStorage'a kaydet
  const saveFiltersToStorage = (filters) => {
    try {
      sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch (error) {
      console.error('Filtreler kaydedilirken hata:', error);
    }
  };
  
  // Başlangıç filtrelerini sessionStorage'dan al
  const initialFilters = loadFiltersFromStorage();
  
  const [searchTerm, setSearchTerm] = useState(initialFilters.searchTerm || '');
  const [statusFilter, setStatusFilter] = useState(initialFilters.statusFilter || '');
  const [methodFilter, setMethodFilter] = useState(initialFilters.methodFilter || '');
  const [startDateFilter, setStartDateFilter] = useState(initialFilters.startDateFilter || '');
  const [endDateFilter, setEndDateFilter] = useState(initialFilters.endDateFilter || '');
  const [showOnlyDuplicates, setShowOnlyDuplicates] = useState(initialFilters.showOnlyDuplicates || false);
  const [mailStatusFilter, setMailStatusFilter] = useState(initialFilters.mailStatusFilter || '');
  
  // Sayfalama için state değişkenleri
  const [currentPage, setCurrentPage] = useState(initialFilters.currentPage || 1);
  const [itemsPerPage] = useState(15);
  
  const [statuses, setStatuses] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [accessiblePaymentMethods, setAccessiblePaymentMethods] = useState([]);
  
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: initialFilters.sortKey || 'created_at',
    direction: initialFilters.sortDirection || 'desc'
  });
  
  // Mail simgesinin renk durumlarını takip etmek için state
  const [activeMailButtons, setActiveMailButtons] = useState({});
  
  // Gösterilecek modallar
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentRefund, setCurrentRefund] = useState(null);
  const [currentImage, setCurrentImage] = useState(null);
  
  // İade verileri ve filtre seçeneklerini yükle
  useEffect(() => {
    fetchStatuses();
    fetchRefunds();
    
    // Doğrudan temel erişim yöntemini kullan, normal API'yi hiç deneme
    if (hasPermission('view_payment_methods')) {
      console.log('Doğrudan temel erişim yöntemi kullanılıyor...');
      // Ödeme yöntemlerini yüklerken asla normal API'yi çağırma, direkt temel erişimi kullan
      fetchPaymentMethodsBasic();
    }
  }, []);
  
  // Durumları yükle
  const fetchStatuses = async () => {
    try {
      const data = await getStatuses();
      setStatuses(data);
    } catch (error) {
      toast.error(`Durum verileri yüklenirken hata: ${error.message}`);
    }
  };
  
  // İadeleri yükle
  const fetchRefunds = async () => {
    setIsRefreshing(true);
    setLoading(true); // İade yüklemesi başladığında loading durumunu true yap
    try {
      const data = await getRefunds();
      setRefunds(data);
      
      // Aynı işlemleri tespit et
      detectDuplicateRefunds(data);
    } catch (error) {
      toast.error(`İade verileri yüklenirken hata: ${error.message}`);
    } finally {
      setLoading(false); // İade yüklemesi bittiğinde loading durumunu false yap
      setIsRefreshing(false);
    }
  };
  
  // Ödeme yöntemlerini temel erişim yöntemiyle yükle
  const fetchPaymentMethodsBasic = async () => {
    setLoading(true); // Yükleme başladığında loading durumunu true yap
    try {
      // İlk olarak, kullanıcının view_payment_methods izni olup olmadığını kontrol et
      if (!hasPermission('view_payment_methods')) {
        setAccessiblePaymentMethods([]);
        setPaymentMethods([]);
        setLoading(false);
        return;
      }
      
      // Direkt olarak temel erişim API'sini kullan
      try {
        const basicResponse = await roleApi.getUserAccessiblePaymentMethodsBasic();
        
        if (Array.isArray(basicResponse) && basicResponse.length > 0) {
          setAccessiblePaymentMethods(basicResponse);
          setPaymentMethods(basicResponse);
          return;
        } else {
          setAccessiblePaymentMethods([]);
          setPaymentMethods([]);
        }
      } catch (basicError) {
        setAccessiblePaymentMethods([]);
        setPaymentMethods([]);
      }
      
    } catch (error) {
      setAccessiblePaymentMethods([]);
      setPaymentMethods([]);
    } finally {
      setLoading(false); // Yükleme bittiğinde loading durumunu false yap
    }
  };
  
  // Arama ve filtreleme
  useEffect(() => {
    if (!refunds) return;
    
    let filtered = [...refunds];
    
    // Arama terimine göre filtrele
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      
      // ID ile filtreleme için # işareti kontrolü
      const idMatch = search.match(/^#(\d+)$/);
      if (idMatch) {
        // # işareti ile başlayan ve sayı olan arama terimi ID araması olarak kabul edilir
        const idToSearch = idMatch[1];
        filtered = filtered.filter(item => item.id.toString() === idToSearch);
      } else {
        // Normal metin araması
        filtered = filtered.filter(
          item =>
            item.user_id?.toLowerCase().includes(search) ||
            item.refund_name?.toLowerCase().includes(search) ||
            item.member_name?.toLowerCase().includes(search) ||  
            item.login_id?.toLowerCase().includes(search) ||
            item.ticket_id?.toLowerCase().includes(search) ||
            (item.reason && item.reason.toLowerCase().includes(search))
        );
      }
    }
    
    // Sadece mükerrer kayıtları göster
    if (showOnlyDuplicates) {
      filtered = filtered.filter(item => duplicateRefundGroups[item.id]);
    }
    
    // Duruma göre filtrele
    if (statusFilter) {
      filtered = filtered.filter(item => item.status_id.toString() === statusFilter);
    }
    
    // Mail durumuna göre filtrele
    if (mailStatusFilter) {
      if (mailStatusFilter === 'sent') {
        filtered = filtered.filter(item => item.mail_sent === 1);
      } else if (mailStatusFilter === 'not_sent') {
        filtered = filtered.filter(item => item.mail_sent === 0 || item.mail_sent === null);
      }
    }
    
    // Ödeme yöntemine göre filtrele
    if (methodFilter) {
      filtered = filtered.filter(item => item.payment_method_id.toString() === methodFilter);
    }
    
    // Başlangıç tarihine göre filtrele
    if (startDateFilter) {
      const startDate = new Date(startDateFilter);
      startDate.setHours(0, 0, 0, 0); // Günün başlangıcını ayarla
      
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.created_at);
        return isAfter(itemDate, startDate) || isEqual(itemDate, startDate);
      });
    }
    
    // Bitiş tarihine göre filtrele
    if (endDateFilter) {
      const endDate = new Date(endDateFilter);
      endDate.setHours(23, 59, 59, 999); // Günün sonunu ayarla
      
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.created_at);
        return isBefore(itemDate, endDate) || isEqual(itemDate, endDate);
      });
    }
    
    // Sıralama
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    setFilteredRefunds(filtered);
    // Filtre değiştiğinde ilk sayfaya dön
    setCurrentPage(1);
  }, [refunds, searchTerm, statusFilter, methodFilter, startDateFilter, endDateFilter, sortConfig, showOnlyDuplicates, duplicateRefundGroups, mailStatusFilter]);
  
  // Resim URL'sini işleyen yardımcı fonksiyon
  const getFormattedImageUrl = (url) => {
    if (!url) {
      return '';
    }
    
    // URL zaten tam yol içeriyorsa olduğu gibi kullan
    if (url.startsWith('http')) {
      return url;
    }
    
    // URL /uploads ile başlıyorsa olduğu gibi kullan (yeni format)
    if (url.startsWith('/uploads/')) {
      return url;
    }
    
    // URL uploads/ ile başlıyorsa / öneki ekle
    if (url.startsWith('uploads/')) {
      return `/${url}`;
    }
    
    // Eski format kontrolü - geçiş dönemi için (client/public içerenler)
    if (url.startsWith('/client/public/')) {
      // /client/public/uploads/... -> /uploads/...
      return url.replace('/client/public/uploads/', '/uploads/').replace('/client/public/', '/');
    }
    
    // Aksi takdirde /uploads/ öneki ekle
    return `/uploads/${url}`;
  };
  
  // Sıralama ayarla
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };
  
  // Durum güncelleme
  const handleUpdateStatus = async (id, newStatusId) => {
    try {
      const response = await updateRefundStatus(id, newStatusId);
      
      // İadeleri güncelle - API'den dönen güncellenmiş veriyi kullan
      setRefunds(
        refunds.map(refund =>
          refund.id === id ? { 
            ...refund, 
            status_id: response.status_id,
            status_name: response.status_name,
            color: response.color
          } : refund
        )
      );
      
      return true;
    } catch (error) {
      throw error;
    }
  };
  
  // İade silme
  const handleDeleteRefund = async (id) => {
    try {
      await deleteRefund(id);
      
      // İadeleri güncelle
      setRefunds(refunds.filter(refund => refund.id !== id));
      
      return true;
    } catch (error) {
      throw error;
    }
  };
  
  // Durum rengi
  const getStatusClassForList = (statusName, color) => {
    if (!color) {
      return getStatusClass(statusName);
    }
    return getStatusClass(statusName, color);
  };
  
  // Durum için style nesnesi
  const getStatusStyleForList = (color) => {
    return getStatusStyle(color);
  };
  
  // Şu anki sayfada gösterilecek verileri hesapla
  const currentItems = useMemo(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return filteredRefunds.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredRefunds, currentPage, itemsPerPage]);
  
  // Toplam sayfa sayısını hesapla
  const totalPages = useMemo(() => Math.ceil(filteredRefunds.length / itemsPerPage), [filteredRefunds, itemsPerPage]);
  
  // Sayfa değiştirme işlevi
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  // Önceki sayfaya gitme işlevi
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  // Sonraki sayfaya gitme işlevi
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  // Sıralama ikonları
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ChevronDown size={16} className="ml-1 text-gray-400" />;
    }
    
    return sortConfig.direction === 'asc' ? (
      <ChevronUp size={16} className="ml-1 text-primary-500" />
    ) : (
      <ChevronDown size={16} className="ml-1 text-primary-500" />
    );
  };
  
  // Filtreleri temizle
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setMethodFilter('');
    setStartDateFilter('');
    setEndDateFilter('');
    setShowOnlyDuplicates(false);
    setMailStatusFilter('');
    setCurrentPage(1);
    setSortConfig({ key: 'created_at', direction: 'desc' });
    
    // SessionStorage'ı da temizle
    try {
      sessionStorage.removeItem(FILTERS_STORAGE_KEY);
    } catch (error) {
      console.error('Filtreler temizlenirken hata:', error);
    }
  };
  
  // Aynı işlemleri tespit et
  const detectDuplicateRefunds = (refundList) => {
    // İadeleri benzersiz tanımlayıcılara göre gruplandır
    const duplicateGroups = {};
    const groupIdCounter = {};
    
    // Tüm refundları tarayarak benzersiz anahtarlar oluştur
    refundList.forEach(refund => {
      // user_id, login_id, yöntem ve tutar birleşiminden oluşan benzersiz anahtar
      const uniqueKey = `${refund.user_id || ''}-${refund.login_id || ''}-${refund.payment_method_id || ''}-${refund.amount}`;
      
      if (!duplicateGroups[uniqueKey]) {
        duplicateGroups[uniqueKey] = [];
      }
      
      duplicateGroups[uniqueKey].push(refund.id);
    });
    
    // Sadece birden fazla eşleşen kayıt olanları filtrele
    const duplicates = {};
    
    Object.keys(duplicateGroups).forEach(key => {
      if (duplicateGroups[key].length > 1) {
        // Her bir tekrar eden grup için benzersiz bir ID oluştur
        // Bu ID'yi daha sonra renk atamak için kullanacağız
        const groupId = Object.keys(groupIdCounter).length + 1;
        groupIdCounter[key] = groupId;
        
        duplicateGroups[key].forEach(refundId => {
          duplicates[refundId] = {
            isDuplicate: true,
            count: duplicateGroups[key].length,
            groupId
          };
        });
      }
    });
    
    setDuplicateRefundGroups(duplicates);
  };
  
  // Tekrarlayan işlemler için stil döndür
  const getDuplicateStyle = (refundId) => {
    if (!duplicateRefundGroups[refundId]) return {};
    
    // Gruplar için farklı renkleri belirleyelim - pastel tonlar kullanarak
    const colors = [
      'rgba(255, 182, 193, 0.2)', // Açık pembe
      'rgba(173, 216, 230, 0.2)', // Açık mavi
      'rgba(152, 251, 152, 0.2)', // Açık yeşil
      'rgba(255, 222, 173, 0.2)', // Açık turuncu
      'rgba(221, 160, 221, 0.2)', // Açık mor
      'rgba(255, 228, 196, 0.2)', // Açık kahverengi
      'rgba(176, 224, 230, 0.2)', // Açık turkuaz
      'rgba(255, 250, 205, 0.2)'  // Açık sarı
    ];
    
    const groupId = duplicateRefundGroups[refundId].groupId;
    const colorIndex = (groupId - 1) % colors.length;
    
    return {
      backgroundColor: colors[colorIndex]
    };
  };
  
  // Mail butonuna tıklanınca renk değişimini sağlayan fonksiyon
  const toggleMailButton = async (refundId) => {
    try {
      // Mevcut durumu al ve tersini ayarla
      const newMailStatus = !activeMailButtons[refundId];
      
      // API'ye mail durumunu güncelleme isteği gönder
      await updateRefundMailStatus(refundId, newMailStatus);
      
      // UI'da buton durumunu güncelle
      setActiveMailButtons(prev => ({
        ...prev,
        [refundId]: newMailStatus
      }));
      
      // İadeler listesini güncelle
      setRefunds(prevRefunds => 
        prevRefunds.map(refund => 
          refund.id === refundId ? 
            { ...refund, mail_sent: newMailStatus ? 1 : 0 } : 
            refund
        )
      );
      
      toast.success(`${newMailStatus ? 'Mail gönderildi' : 'Mail durumu iptal edildi'}`);
    } catch (error) {
      toast.error(`Mail durumu güncellenirken hata: ${error.message}`);
    }
  };
  
  // Verileri yükledikten sonra aktif mail butonlarını belirle
  useEffect(() => {
    if (refunds && refunds.length > 0) {
      const mailButtons = {};
      refunds.forEach(refund => {
        if (refund.mail_sent === 1) {
          mailButtons[refund.id] = true;
        }
      });
      setActiveMailButtons(mailButtons);
    }
  }, [refunds]);
  
  // Filtreleri sessionStorage'a kaydet
  useEffect(() => {
    const filtersToSave = {
      searchTerm,
      statusFilter,
      methodFilter,
      startDateFilter,
      endDateFilter,
      showOnlyDuplicates,
      mailStatusFilter,
      currentPage,
      sortKey: sortConfig.key,
      sortDirection: sortConfig.direction
    };
    
    saveFiltersToStorage(filtersToSave);
  }, [searchTerm, statusFilter, methodFilter, startDateFilter, endDateFilter, showOnlyDuplicates, mailStatusFilter, currentPage, sortConfig]);
  
  // Yükleme görünümü
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="py-6 max-w-full">
      <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">İade İşlemleri</h1>
          <p className="text-gray-600">Sisteme kayıtlı tüm iade işlemlerini görüntüleyin ve yönetin.</p>
        </div>
        
        <div className="flex items-center space-x-2 mt-3 sm:mt-0">
          <PermissionGuard requiredPermissions={['create_refund']}>
            <Link
              to="/refunds/create"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 flex items-center"
            >
              <PlusCircle size={16} className="mr-1" />
              Yeni İade
            </Link>
          </PermissionGuard>
          
          <button
            onClick={fetchRefunds}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 flex items-center"
            disabled={isRefreshing}
          >
            <RefreshCw size={16} className={`mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
            Yenile
          </button>
        </div>
      </div>
      
      {/* Filtreler */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Arama */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Search size={14} className="inline mr-1" />
              Ara
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Kullanıcı adı, işlem adı, #ID..."
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
            <div className="mt-1 text-xs text-gray-500 flex items-center">
              <Info size={12} className="mr-1" />
              İade ID'si ile arama yapmak için # işaretini kullanın. Örn: #116
            </div>
          </div>
          
          {/* Tarih Filtreleri */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar size={14} className="inline mr-1" />
              Başlangıç Tarihi
            </label>
            <input
              type="date"
              value={startDateFilter}
              onChange={(e) => setStartDateFilter(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar size={14} className="inline mr-1" />
              Bitiş Tarihi
            </label>
            <input
              type="date"
              value={endDateFilter}
              onChange={(e) => setEndDateFilter(e.target.value)}
              min={startDateFilter} // Başlangıç tarihinden önce seçilememesi için
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
          
          {/* Durum Filtresi */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Filter size={14} className="inline mr-1" />
              Durum
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="">Tüm Durumlar</option>
              {statuses.map(status => (
                <option key={status.id} value={status.id.toString()}>
                  {status.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* İkinci satırdaki filtreler */}
          <div className="md:col-span-2">
            {/* Ödeme Yöntemi Filtresi */}
            {hasPermission('view_payment_methods') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Filter size={14} className="inline mr-1" />
                  Ödeme Yöntemi
                </label>
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 border-t-2 border-primary-500 rounded-full animate-spin"></div>
                    <span className="text-sm text-gray-500">Ödeme yöntemleri yükleniyor...</span>
                  </div>
                ) : accessiblePaymentMethods && accessiblePaymentMethods.length > 0 ? (
                  <select
                    value={methodFilter}
                    onChange={(e) => setMethodFilter(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  >
                    <option value="">Tüm Yöntemler</option>
                    {accessiblePaymentMethods.map(method => (
                      <option key={method.id} value={method.id.toString()}>
                        {method.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="text-sm text-gray-500 border border-gray-200 rounded-md p-2 bg-gray-50">
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <span>Ödeme yöntemi listesi alınamadı</span>
                    </div>
                    <p className="mt-1 ml-7 text-xs text-gray-500">
                      Şu anda ödeme yöntemlerine göre filtreleme yapılamıyor. Lütfen daha sonra tekrar deneyin veya yöneticinizle iletişime geçin.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Mail Durumu Filtresi */}
          {hasPermission('view_all_refunds') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Mail size={14} className="inline mr-1" />
                Mail Durumu
              </label>
              <select
                value={mailStatusFilter}
                onChange={(e) => setMailStatusFilter(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="">Tüm Kayıtlar</option>
                <option value="sent">Mail Gönderilen</option>
                <option value="not_sent">Mail Gönderilmeyen</option>
              </select>
            </div>
          )}
          
          {/* Mükerrer İşlemleri Göster */}
          <div className="md:col-span-1 flex items-end">
            <div className="w-full p-2 border border-gray-300 rounded-md bg-gray-50">
              <label className="flex items-center cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showOnlyDuplicates}
                  onChange={(e) => setShowOnlyDuplicates(e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="ml-2 flex items-center text-sm font-medium text-gray-700">
                  <Copy size={14} className="mr-1 text-amber-500" />
                  Sadece DUP işlemleri göster
                </span>
                {Object.keys(duplicateRefundGroups).length > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs rounded-full">
                    {Object.keys(duplicateRefundGroups).length}
                  </span>
                )}
              </label>
            </div>
          </div>
          
          {/* Filtre temizleme */}
          <div className="md:col-span-1 flex items-end">
            <button
              onClick={clearFilters}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 w-full flex items-center justify-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Filtreleri Temizle
            </button>
          </div>
        </div>
      </div>
      
      {/* İade Listesi */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-auto w-full" ref={tableContainerRef}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center">
                    Tarih {getSortIcon('created_at')}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('user_id')}
                >
                  <div className="flex items-center">
                    USER ID {getSortIcon('user_id')}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('login_id')}
                >
                  <div className="flex items-center">
                    Login ID {getSortIcon('login_id')}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('member_name')}
                >
                  <div className="flex items-center">
                    Üye İsmi {getSortIcon('member_name')}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('refund_name')}
                >
                  <div className="flex items-center">
                    İade Edilecek {getSortIcon('refund_name')}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('reason')}
                >
                  <div className="flex items-center">
                    İade Sebebi {getSortIcon('reason')}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('amount')}
                >
                  <div className="flex items-center">
                    Tutar {getSortIcon('amount')}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('status_name')}
                >
                  <div className="flex items-center">
                    Durum {getSortIcon('status_name')}
                  </div>
                </th>
                <PermissionGuard permission={["view_payment_methods"]}>
                  <th
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('payment_method_id')}
                  >
                    <div className="flex items-center">
                      Ödeme Yöntemi {getSortIcon('payment_method_id')}
                    </div>
                  </th>
                </PermissionGuard>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.length > 0 ? (
                currentItems.map(refund => (
                  <tr 
                    key={refund.id} 
                    className="hover:bg-gray-50 group relative" 
                    style={getDuplicateStyle(refund.id)}
                    onMouseEnter={(e) => {
                      try {
                        // İşlem butonlarını satırın sağında göster
                        const refundId = refund.id;
                        if (!refundId) return;
                        
                        const actionButtons = document.getElementById(`actions-${refundId}`);
                        const tableContainer = tableContainerRef.current;
                        
                        if (actionButtons && tableContainer && e.currentTarget) {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const tableRect = tableContainer.getBoundingClientRect();
                          actionButtons.style.top = `${rect.top + rect.height/2}px`;
                          actionButtons.style.left = `${tableRect.right - 5}px`;
                          actionButtons.style.opacity = '1';
                          actionButtons.style.visibility = 'visible';
                        }
                      } catch (error) {
                        console.error('İşlem butonlarını konumlandırırken hata:', error);
                      }
                    }}
                    onMouseLeave={(e) => {
                      try {
                        // Satırdan çıkınca hemen gizle, gecikme yok
                        const refundId = refund.id;
                        if (!refundId) return;
                        
                        const actionButtons = document.getElementById(`actions-${refundId}`);
                        if (actionButtons) {
                          actionButtons.style.opacity = '0';
                          actionButtons.style.visibility = 'hidden';
                        }
                      } catch (error) {
                        console.error('İşlem butonlarını gizlerken hata:', error);
                      }
                    }}
                  >
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900 flex items-center">
                        {hasPermission('view_all_refunds') && (
                          <button 
                            onClick={() => toggleMailButton(refund.id)}
                            className={`mr-1 focus:outline-none transition-colors duration-200 ${activeMailButtons[refund.id] ? 'text-green-500' : 'text-gray-400 hover:text-gray-600'}`}
                            title={activeMailButtons[refund.id] ? "Mail Gönderildi" : "Mail Gönder"}
                          >
                            <Mail size={16} />
                          </button>
                        )}
                        <div>
                          {format(new Date(refund.created_at), 'dd MMM yyyy', { locale: tr })}
                          <div className="text-xs text-gray-500">
                            {format(new Date(refund.created_at), 'HH:mm', { locale: tr })}
                          </div>
                        </div>
                        {duplicateRefundGroups[refund.id] && (
                          <span title={`Bu işlemin ${duplicateRefundGroups[refund.id].count - 1} adet benzer kaydı bulundu. (Aynı kullanıcı, login ID, ödeme yöntemi ve tutar)`} className="ml-1">
                            <AlertTriangle size={16} className="text-amber-500" />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {refund.user_id || '-'}
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {refund.login_id || '-'}
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div 
                        className="text-sm text-gray-900"
                      >
                        {refund.member_name || '-'}
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div 
                        className="text-sm text-gray-900"
                      >
                        {refund.refund_name || '-'}
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {refund.reason || '-'}
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {refund.amount.toLocaleString('tr-TR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })} TL
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClassForList(refund.status_name, refund.color)}`} style={getStatusStyleForList(refund.color)}>
                        {refund.status_name}
                      </div>
                    </td>
                    <PermissionGuard permission={["view_payment_methods"]}>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {refund.payment_method_name || '-'}
                        </div>
                      </td>
                    </PermissionGuard>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="30" className="px-3 py-10 text-center text-gray-500">
                    {currentItems.length === 0 && refunds.length > 0 ? (
                      <div className="flex flex-col items-center justify-center">
                        <Info size={36} className="text-gray-400 mb-2" />
                        <h3 className="text-lg font-medium text-gray-900">Filtrelere uygun kayıt bulunamadı</h3>
                        <p className="mt-1 text-gray-500">Lütfen arama kriterlerinizi değiştirin ya da filtrelerinizi temizleyin.</p>
                        <button
                          onClick={clearFilters}
                          className="mt-3 bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                          Filtreleri Temizle
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center">
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          className="h-12 w-12 text-gray-400 mb-3" 
                          fill="none" 
                          viewBox="0 0 24 24" 
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                        <h3 className="text-lg font-medium text-gray-900">Henüz iade kaydı bulunmuyor</h3>
                        <p className="mt-1 text-gray-500">Yeni iade işlemleri ekleyerek başlayabilirsiniz.</p>
                        <PermissionGuard requiredPermissions={['create_refund']}>
                          <Link
                            to="/refunds/create"
                            className="mt-3 bg-primary-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 flex items-center"
                          >
                            <Plus size={16} className="mr-1" />
                            Yeni İade Ekle
                          </Link>
                        </PermissionGuard>
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Tablonun dışında, sağ tarafta görünecek işlem butonları */}
      {currentItems.filter(refund => refund && refund.id).map(refund => (
        <div 
          key={`actions-${refund.id}`}
          id={`actions-${refund.id}`}
          className="fixed z-50 opacity-0 invisible flex"
          style={{ transform: 'translateY(-50%)', transition: 'opacity 0.1s, visibility 0.1s' }}
          onMouseEnter={(e) => {
            try {
              // Butonların üzerindeyken gizleme
              if (e.currentTarget) {
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.visibility = 'visible';
              }
            } catch (error) {
              console.error('Butonları gösterirken hata:', error);
            }
          }}
          onMouseLeave={(e) => {
            try {
              // Butonlardan çıkınca hemen gizle
              if (e.currentTarget) {
                e.currentTarget.style.opacity = '0';
                e.currentTarget.style.visibility = 'hidden';
              }
            } catch (error) {
              console.error('Butonları gizlerken hata:', error);
            }
          }}
        >
          <div className="flex items-center">
            {/* Satırla butonlar arasında daha dar tıklanabilir alan */}
            <div className="w-3 h-12 cursor-pointer"></div>
            <div className="flex shadow-md rounded-md bg-white border border-gray-200">
              <Link
                to={`/refunds/${refund.id}`}
                className="text-primary-600 hover:text-primary-900 p-2 hover:bg-gray-50 rounded-l-md flex items-center"
                title="Detay"
              >
                <Eye size={16} />
              </Link>
              
              <PermissionGuard requiredPermissions={['update_refund_status']}>
                <button
                  onClick={() => {
                    setCurrentRefund(refund);
                    setShowStatusModal(true);
                  }}
                  className="text-blue-600 hover:text-blue-900 p-2 hover:bg-gray-50 border-l border-gray-200 flex items-center"
                  title="Durum Güncelle"
                >
                  <Edit size={16} />
                </button>
              </PermissionGuard>
              
              {(() => {
                const imagePath = 
                  refund.receipt_path || 
                  refund.receipt_image_path ||
                  refund.receipt_image ||
                  refund.image_path ||
                  refund.receipt;
                
                return imagePath ? (
                  <button
                    onClick={() => {
                      setCurrentImage(getFormattedImageUrl(imagePath));
                      setShowImageModal(true);
                    }}
                    className="text-green-600 hover:text-green-900 p-2 hover:bg-gray-50 border-l border-gray-200 flex items-center"
                    title="Dekont Görüntüle"
                  >
                    <Image size={16} />
                  </button>
                ) : null;
              })()}
              
              <PermissionGuard requiredPermissions={['delete_refund']}>
                <button
                  onClick={() => {
                    setCurrentRefund(refund);
                    setShowDeleteModal(true);
                  }}
                  className="text-red-600 hover:text-red-900 p-2 hover:bg-gray-50 border-l border-gray-200 rounded-r-md flex items-center"
                  title="Sil"
                >
                  <Trash2 size={16} />
                </button>
              </PermissionGuard>
            </div>
          </div>
        </div>
      ))}
      
      {/* Bilgi Gösterimi */}
      <div className="mt-4 text-sm text-gray-500 flex flex-col sm:flex-row sm:justify-between sm:items-center">
        <div>
          {startDateFilter && endDateFilter && (
            <span className="text-primary-600">
              <Calendar size={14} className="inline mr-1" />
              {format(new Date(startDateFilter), 'dd MMM yyyy', { locale: tr })} - {format(new Date(endDateFilter), 'dd MMM yyyy', { locale: tr })} tarih aralığı filtreleniyor
            </span>
          )}
          {Object.keys(duplicateRefundGroups).length > 0 && (
            <span className={`ml-3 ${showOnlyDuplicates ? 'text-amber-700 font-medium' : 'text-amber-600'}`}>
              <AlertTriangle size={14} className="inline mr-1" />
              Benzer işlemleri içeren {Math.floor(Object.keys(duplicateRefundGroups).length / 2)} grup bulundu
              {showOnlyDuplicates && ' (şu an sadece aynı işlemler gösteriliyor)'}
            </span>
          )}
        </div>
        <div className="mt-2 sm:mt-0 flex items-center">
          <span>Toplam: {filteredRefunds.length} kayıt {refunds.length !== filteredRefunds.length ? `(filtrelenmiş: ${filteredRefunds.length}/${refunds.length})` : ''}</span>
        </div>
      </div>
      
      {/* Sayfalama Kontrolleri */}
      {filteredRefunds.length > itemsPerPage && (
        <div className="mt-4 flex items-center justify-center">
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-2 py-2 rounded-l-md border ${currentPage === 1 ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'}`}
            >
              <span className="sr-only">Önceki</span>
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>
            
            {/* Sayfa numaraları */}
            {Array.from({ length: Math.min(5, totalPages) }).map((_, index) => {
              // Sayfa numaralarını hesapla
              let pageNumber;
              if (totalPages <= 5) {
                pageNumber = index + 1;
              } else if (currentPage <= 3) {
                pageNumber = index + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNumber = totalPages - 4 + index;
              } else {
                pageNumber = currentPage - 2 + index;
              }
              
              // Eğer hesaplanan sayfa numarası geçerli aralıkta değilse, gösterme
              if (pageNumber < 1 || pageNumber > totalPages) return null;
              
              return (
                <button
                  key={pageNumber}
                  onClick={() => paginate(pageNumber)}
                  className={`relative inline-flex items-center px-4 py-2 border ${currentPage === pageNumber ? 'z-10 bg-primary-50 border-primary-500 text-primary-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`}
                >
                  {pageNumber}
                </button>
              );
            })}
            
            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className={`relative inline-flex items-center px-2 py-2 rounded-r-md border ${currentPage === totalPages ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'}`}
            >
              <span className="sr-only">Sonraki</span>
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </button>
          </nav>
          <span className="ml-4 text-sm text-gray-500">
            Sayfa {currentPage} / {totalPages} (toplam {filteredRefunds.length} kayıt)
          </span>
        </div>
      )}
      
      {/* Modallar */}
      {showStatusModal && (
        <StatusUpdateModal
          refund={currentRefund}
          onClose={() => setShowStatusModal(false)}
          onUpdate={handleUpdateStatus}
          statuses={statuses}
        />
      )}
      
      {showImageModal && (
        <ImageViewerModal
          imageUrl={currentImage}
          onClose={() => setShowImageModal(false)}
        />
      )}
      
      {showDeleteModal && (
        <DeleteConfirmModal
          refund={currentRefund}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteRefund}
        />
      )}
    </div>
  );
};

export default RefundListPage;